import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  ListSubheader,
  InputLabel,
  MenuItem,
  Stack,
  Switch,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { Lock as LockIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import {
  exercisesApi,
  isPremiumExerciseType,
  revealLevelFromSetting,
  type Exercise,
  type ExerciseLevel,
  type ExerciseRevealLevel,
  type ExerciseStatus,
  type ExerciseType,
} from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'
import { EXERCISE_TYPE_GROUPS, isTeacherChecked, type ExerciseTypeGroup } from './catalog'
import { defaultContentFor } from './contentTypes'
import { ExerciseContentEditor } from './editors/ExerciseContentEditor'
import { ExercisePreview } from './ExercisePreview'
import type { ThemeWord } from './themeWords'

/**
 * Конструктор задания: выбор типа, содержимое по типу и предпросмотр «глазами
 * ученика».
 *
 * Тип задаётся только при создании — сменить его у существующего задания
 * нельзя: содержимое одного типа не превращается в содержимое другого, а на
 * задание уже могут ссылаться попытки учеников.
 */

/** Порядок вариантов — от закрытого к открытому. */
const REVEAL_LEVELS: ExerciseRevealLevel[] = ['none', 'verdict', 'answers']

type Props = {
  open: boolean
  /**
   * Тема программы, в которую сразу встанет новое задание. `null` — режим
   * банка: задание сохраняется само по себе и ставится в темы потом. Без этого
   * режима завести упражнение можно было только через программу.
   */
  themeId?: number | null
  exercise: Exercise | null
  themeWords?: ThemeWord[]
  /** Премиум-типы недоступны без параметра подписки — показываем замок */
  premiumEnabled: boolean
  /** Только просмотр: задание системной программы правит платформа, не учитель */
  readOnly?: boolean
  onClose: () => void
  /**
   * Переключить шаг на другой тип блока — вход в диалог один на все типы.
   * Только для режима темы: в банке блоков теории и словаря не существует.
   */
  onSwitchType?: (next: 'theory' | 'vocabulary') => void
  onSaved: (exercise: Exercise) => void
}

export function ExerciseFormDialog({
  open,
  themeId = null,
  exercise,
  themeWords = [],
  premiumEnabled,
  readOnly = false,
  onClose,
  onSwitchType,
  onSaved,
}: Props) {
  const { t } = useTranslation()

  const [type, setType] = useState<ExerciseType>('choice')
  const [title, setTitle] = useState('')
  const [level, setLevel] = useState<ExerciseLevel>('medium')
  const [instructions, setInstructions] = useState('')
  const [status, setStatus] = useState<ExerciseStatus>('draft')
  const [content, setContent] = useState<Record<string, unknown>>(() => defaultContentFor('choice'))
  const [preview, setPreview] = useState(false)
  // Просмотр: по умолчанию задание глазами ученика, конфигурация — по кнопке
  const [showConfig, setShowConfig] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Сколько ученику видно после сдачи. Дефолты разные: за зачётную работу
  // честно сказать «верно/неверно», а тренировку ученик перерешивает сколько
  // угодно — эталоны там превращают задание в подбор ответов перебором.
  const [revealGraded, setRevealGraded] = useState<ExerciseRevealLevel>('verdict')
  const [revealTraining, setRevealTraining] = useState<ExerciseRevealLevel>('none')

  useEffect(() => {
    if (!open) return

    if (exercise) {
      setType(exercise.type)
      setTitle(exercise.title)
      setLevel(exercise.level)
      setInstructions(exercise.instructions ?? '')
      setStatus(exercise.status)
      setContent(exercise.content)
      // Легаси-значения с сервера раскладываем по той же таблице, что и сервер:
      // задания, созданные до появления уровней, открываются осмысленно.
      setRevealGraded(revealLevelFromSetting(exercise.settings?.show_correct_after, 'verdict'))
      setRevealTraining(revealLevelFromSetting(exercise.settings?.show_correct_after_training, 'none'))
    } else {
      setType('choice')
      setTitle('')
      setLevel('medium')
      setInstructions('')
      setStatus('draft')
      setContent(defaultContentFor('choice'))
      setRevealGraded('verdict')
      setRevealTraining('none')
    }

    setPreview(false)
    setError(null)
  }, [open, exercise])

  const changeType = (next: ExerciseType) => {
    setType(next)
    // Содержимое прежнего типа новому не подходит — начинаем с заготовки
    setContent(defaultContentFor(next))
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        title,
        level,
        instructions: instructions || null,
        content,
        status,
        // Настройки едут вместе с заданием: сохранение переводит легаси-значения
        // в новые, отдельной миграции для этого не нужно.
        settings: {
          ...(exercise?.settings ?? {}),
          show_correct_after: revealGraded,
          show_correct_after_training: revealTraining,
        },
      }
      let saved: Exercise

      if (exercise) {
        saved = await exercisesApi.update(exercise.id, payload)
      } else if (themeId != null) {
        saved = await exercisesApi.create(themeId, { ...payload, type })
      } else {
        saved = await exercisesApi.createInBank({ ...payload, type })
      }

      onSaved(saved)
      onClose()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const typeLocked = exercise !== null
  const premiumBlocked = isPremiumExerciseType(type) && !premiumEnabled

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {/* Тип в шапке: у существующего задания он не меняется, и отдельное
            поле было мёртвым контролом */}
        {exercise ? t(`exercises.types.${type}.name`) : t('exercises.editor.createTitle')}
        {exercise && (
          <Typography variant="body2" color="text.secondary">
            {exercise.title}
          </Typography>
        )}
      </DialogTitle>

      {/* Тип блока — тот же шаг, что и в диалоге теории: выбрав «Задание»,
        учитель должен иметь право передумать, не закрывая форму */}
      {onSwitchType && themeId != null && !exercise && !readOnly && (
        <Box sx={{ px: 3, pt: 1, pb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('programs.fields.elementType')}</InputLabel>
            <Select
              label={t('programs.fields.elementType')}
              value="exercise"
              onChange={(event) => {
                const next = event.target.value as 'theory' | 'vocabulary' | 'exercise'

                if (next !== 'exercise') onSwitchType(next)
              }}
            >
              <MenuItem value="theory">{t('programs.elementTypes.theory')}</MenuItem>
              <MenuItem value="vocabulary">{t('programs.elementTypes.vocabulary')}</MenuItem>
              <MenuItem value="exercise">{t('programs.elementTypes.exercise')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}
      <DialogContent dividers>
        {readOnly ? (
          // Просмотр — вид, а не форма: поля ввода ловят фокус на клик и
          // выглядят как приглашение править то, что править нельзя
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={t(`exercises.levels.${level}`)} />
              {/* «Готово» — норма, а не новость: бейдж нужен только там, где
                  задание ученику не выдать. Так же сделано в панели урока */}
              {status !== 'ready' && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={status === 'draft' ? 'warning' : 'default'}
                  label={t(`exercises.exerciseStatus.${status}`)}
                />
              )}
            </Stack>

            {instructions.trim() !== '' && <Typography>{instructions}</Typography>}

            {isTeacherChecked(type) && <Alert severity="info">{t('exercises.editor.teacherCheckedType')}</Alert>}

            <Divider />

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {t('exercises.editor.content')}
              </Typography>
              {/* По умолчанию — вид ученика: конструктор с заблокированными
                  полями это форма, а не задание. Конфигурация с эталонами
                  открывается по явной просьбе учителя */}
              <Chip
                size="small"
                clickable
                color={showConfig ? 'primary' : 'default'}
                label={t('exercises.editor.showConfig')}
                onClick={() => setShowConfig((value) => !value)}
              />
            </Stack>

            {showConfig ? (
              // Конфигурацию открывают, чтобы прочитать содержимое: эталоны
              // пишем обычным цветом текста, а не приглушённым цветом
              // выключенного поля из темы. Подложку и подписи даёт тема,
              // «выключенность» остаётся за ними и курсором
              <Box
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: 'unset',
                    color: 'text.primary',
                    cursor: 'default',
                  },
                  '& .MuiChip-root.Mui-disabled': { opacity: 1 },
                }}
              >
                <ExerciseContentEditor
                  type={type}
                  content={content}
                  onChange={setContent}
                  themeWords={themeWords}
                  exerciseId={exercise?.id ?? null}
                  disabled
                />
              </Box>
            ) : (
              <ExercisePreview type={type} content={content} />
            )}
          </Stack>
        ) : (
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Задание живёт в банке и стоит ссылкой в темах и домашках: без этой
              строки учитель правит его как «своё в этой теме» и не ожидает,
              что изменится и у других учеников */}
          {exercise !== null && (
            <Alert severity="info">{t('exercises.editor.bankScopeHint')}</Alert>
          )}

          <TextField
            select
            size="small"
            label={t('exercises.editor.type')}
            value={type}
            disabled={typeLocked || readOnly}
            helperText={typeLocked ? t('exercises.editor.typeLocked') : t(`exercises.types.${type}.hint`)}
            onChange={(event) => changeType(event.target.value as ExerciseType)}
          >
            {(Object.keys(EXERCISE_TYPE_GROUPS) as ExerciseTypeGroup[]).flatMap((group) => [
              <ListSubheader key={group}>{t(`exercises.groups.${group}`)}</ListSubheader>,
              ...EXERCISE_TYPE_GROUPS[group].map((option) => (
                <MenuItem key={option} value={option} disabled={isPremiumExerciseType(option) && !premiumEnabled}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{t(`exercises.types.${option}.name`)}</span>
                    {isPremiumExerciseType(option) && !premiumEnabled && <LockIcon fontSize="small" color="disabled" />}
                  </Stack>
                </MenuItem>
              )),
            ])}
          </TextField>

          {premiumBlocked && <Alert severity="warning">{t('exercises.editor.premiumRequired')}</Alert>}

          <TextField
            fullWidth
            size="small"
            label={t('exercises.editor.title')}
            value={title}
            slotProps={{ input: { readOnly } }}
            onChange={(event) => setTitle(event.target.value)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              size="small"
              label={t('exercises.editor.level')}
              value={level}
              disabled={readOnly}
              sx={{ minWidth: 180 }}
              onChange={(event) => setLevel(event.target.value as ExerciseLevel)}
            >
              <MenuItem value="low">{t('exercises.levels.low')}</MenuItem>
              <MenuItem value="medium">{t('exercises.levels.medium')}</MenuItem>
              <MenuItem value="high">{t('exercises.levels.high')}</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={status === 'ready'}
                  disabled={readOnly}
                  onChange={(event) => setStatus(event.target.checked ? 'ready' : 'draft')}
                />
              }
              label={t('exercises.editor.ready')}
            />
          </Stack>

          <TextField
            fullWidth
            size="small"
            multiline
            label={t('exercises.editor.instructions')}
            value={instructions}
            slotProps={{ input: { readOnly } }}
            onChange={(event) => setInstructions(event.target.value)}
          />

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              {t('exercises.editor.revealSection')}
            </Typography>
          </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              size="small"
              label={t('exercises.editor.revealGraded')}
              value={revealGraded}
              disabled={readOnly}
              onChange={(event) => setRevealGraded(event.target.value as ExerciseRevealLevel)}
            >
              {REVEAL_LEVELS.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`exercises.editor.revealLevels.${option}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label={t('exercises.editor.revealTraining')}
              value={revealTraining}
              disabled={readOnly}
              onChange={(event) => setRevealTraining(event.target.value as ExerciseRevealLevel)}
            >
              {REVEAL_LEVELS.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`exercises.editor.revealLevels.${option}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {t('exercises.editor.revealHint')}
          </Typography>

          {isTeacherChecked(type) && <Alert severity="info">{t('exercises.editor.teacherCheckedType')}</Alert>}

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {t('exercises.editor.content')}
            </Typography>
            <Chip
              size="small"
              clickable
              color={preview ? 'primary' : 'default'}
              label={t('exercises.editor.preview')}
              onClick={() => setPreview((value) => !value)}
            />
          </Stack>

          <Box>
            {preview ? (
              <ExercisePreview type={type} content={content} />
            ) : (
              <ExerciseContentEditor
                type={type}
                content={content}
                onChange={setContent}
                themeWords={themeWords}
                exerciseId={exercise?.id ?? null}
                disabled={premiumBlocked || readOnly}
              />
            )}
          </Box>
        </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? t('common.close') : t('common.cancel')}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            disabled={saving || premiumBlocked || title.trim() === ''}
            onClick={() => void save()}
          >
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
